/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Add local dependencies.
import { AppDispatch } from '../store';
import {
  setCurrentStudio,
  clearCurrentStudio,
  setLoading,
  setError,
  clearError,
  selectCurrentStudio,
  selectUserRole,
  selectStudioPermissions,
  selectRecentStudios,
  selectStudioContextLoading,
  selectStudioContextError,
} from '../features/studios/studioContextSlice';
import { selectIsAuthenticated } from '../features/auth/authSlice';
import {
  useSwitchStudioMutation,
  useGetCurrentContextQuery,
  useGetUserStudiosQuery,
} from '../features/studios/studiosApi';
import { Studio, StudioRole, StudioPermissions } from '../types/studio';

// Define the studio context value interface
interface StudioContextValue {
  // Current state
  currentStudio: Studio | null;
  userRole: StudioRole | null;
  permissions: StudioPermissions | null;
  recentStudios: Studio[];
  isLoading: boolean;
  error: string | null;

  // Actions
  switchStudio: (studioId: string) => Promise<void>;
  refreshStudio: () => Promise<void>;
  clearStudio: () => void;
  clearContextError: () => void;

  // Utilities
  hasStudioAccess: (studioId: string) => boolean;
  canAccessFeature: (feature: keyof StudioPermissions) => boolean;
}

// Create the studio context
const StudioContext = createContext<StudioContextValue | undefined>(undefined);

// Studio provider props
interface StudioProviderProps {
  children: React.ReactNode;
}

/**
 * Studio context provider component that manages studio state and switching logic.
 */
export const StudioProvider: React.FC<StudioProviderProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { studioId: urlStudioId } = useParams<{ studioId: string }>();

  // Redux selectors
  const currentStudio = useSelector(selectCurrentStudio);
  const userRole = useSelector(selectUserRole);
  const permissions = useSelector(selectStudioPermissions);
  const recentStudios = useSelector(selectRecentStudios);
  const isLoading = useSelector(selectStudioContextLoading);
  const error = useSelector(selectStudioContextError);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // RTK Query hooks
  const [switchStudioMutation] = useSwitchStudioMutation();

  // First check if user has any studios to avoid 404 errors
  const { data: userStudiosData } = useGetUserStudiosQuery(undefined, {
    skip: !isAuthenticated,
  });

  // Memoize skip condition calculation to prevent unnecessary recalculations
  const skipConditions = useMemo(() => {
    const hasStudios = userStudiosData?.total
      ? userStudiosData.total > 0
      : false;
    const isOnStudioPage = Boolean(urlStudioId);

    // Define pages that actually need current studio context
    const currentPath = location.pathname;
    const pagesNeedingStudioContext = [
      '/dashboard', // Main dashboard may need current studio context
      '/tools', // Tools page may need current studio context
      '/billing', // Billing may need current studio context
    ];

    // Check if current page needs studio context
    const pageNeedsStudioContext = pagesNeedingStudioContext.some(path =>
      currentPath.startsWith(path)
    );

    // Skip the context query if:
    // 1. User is not authenticated
    // 2. User has no studios
    // 3. We're on a studio-specific page (studio data comes from individual studio API)
    // 4. Current page doesn't need studio context (landing, auth pages, etc.)
    const shouldSkip =
      !isAuthenticated ||
      !hasStudios ||
      isOnStudioPage ||
      !pageNeedsStudioContext;

    return {
      isAuthenticated: !!isAuthenticated,
      hasStudios,
      userStudiosTotal: userStudiosData?.total || 0,
      isOnStudioPage,
      pageNeedsStudioContext,
      currentPath,
      urlStudioId: urlStudioId || 'none',
      currentStudioId: currentStudio?.id || 'none',
      shouldSkipContextQuery: shouldSkip,
    };
  }, [
    isAuthenticated,
    userStudiosData?.total,
    urlStudioId,
    currentStudio?.id,
    location.pathname,
  ]);

  const {
    data: contextData,
    refetch: refetchContext,
    isLoading: contextLoading,
    error: contextError,
  } = useGetCurrentContextQuery(undefined, {
    skip: skipConditions.shouldSkipContextQuery,
  });

  // Switch studio function
  const switchStudio = useCallback(
    async (studioId: string) => {
      try {
        dispatch(setLoading(true));
        dispatch(clearError());

        // Call the backend to switch studio context
        const studio = await switchStudioMutation(studioId).unwrap();

        // After switching, we need to get the current context to get role and permissions
        // The backend switch endpoint only returns the studio data
        // We'll rely on the getCurrentContext query to provide the full context
        dispatch(
          setCurrentStudio({
            studio,
            role: null, // Will be updated by getCurrentContext
            permissions: null, // Will be updated by getCurrentContext
          })
        );

        // Update URL if needed
        if (urlStudioId !== studioId) {
          navigate(`/studios/${studioId}`, { replace: true });
        }

        // Persist studio selection
        localStorage.setItem('currentStudioId', studioId);

        // Trigger a refetch of the current context to get role and permissions
        refetchContext();
      } catch (err) {
        const errorMessage =
          err && typeof err === 'object' && 'data' in err
            ? (err.data as { message?: string })?.message ||
              'Failed to switch studio'
            : 'Failed to switch studio';
        dispatch(setError(errorMessage));
        throw new Error(errorMessage);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, navigate, switchStudioMutation, urlStudioId, refetchContext]
  );

  // Refresh current studio context
  const refreshStudio = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());
      await refetchContext();
    } catch (err) {
      const errorMessage = 'Failed to refresh studio context';
      dispatch(setError(errorMessage));
      throw new Error(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, refetchContext]);

  // Clear studio context
  const clearStudio = useCallback(() => {
    dispatch(clearCurrentStudio());
    localStorage.removeItem('currentStudioId');
  }, [dispatch]);

  // Clear context error
  const clearContextError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Check if user has access to a studio
  const hasStudioAccess = useCallback(
    (studioId: string) =>
      recentStudios.some(studio => studio.id === studioId) ||
      currentStudio?.id === studioId,
    [recentStudios, currentStudio]
  );

  // Check if user can access a specific feature
  const canAccessFeature = useCallback(
    (feature: keyof StudioPermissions) => permissions?.[feature] ?? false,
    [permissions]
  );

  // Initialize studio context from URL or localStorage
  useEffect(() => {
    // Only initialize if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    const initializeStudio = async () => {
      // If we have context data from the API, use it
      if (contextData && !currentStudio && contextData.currentStudio) {
        dispatch(
          setCurrentStudio({
            studio: contextData.currentStudio,
            role: contextData.userRole!,
            permissions: contextData.permissions,
          })
        );
        return;
      }

      // If URL has a studio ID but no current studio, try to switch
      if (urlStudioId && (!currentStudio || currentStudio.id !== urlStudioId)) {
        try {
          await switchStudio(urlStudioId);
        } catch (err) {
          console.error('Failed to initialize studio from URL:', err);
        }
        return;
      }

      // If no URL studio ID but we have a stored studio ID, try to restore
      const storedStudioId = localStorage.getItem('currentStudioId');
      if (storedStudioId && !currentStudio && !urlStudioId) {
        try {
          await switchStudio(storedStudioId);
          navigate(`/studios/${storedStudioId}`, { replace: true });
        } catch (err) {
          console.error('Failed to restore studio from localStorage:', err);
          localStorage.removeItem('currentStudioId');
        }
      }
    };

    initializeStudio();
  }, [
    isAuthenticated,
    contextData,
    currentStudio,
    urlStudioId,
    switchStudio,
    navigate,
    dispatch,
  ]);

  // Handle context loading and errors
  useEffect(() => {
    if (contextLoading) {
      dispatch(setLoading(true));
    } else {
      dispatch(setLoading(false));
    }

    if (contextError) {
      // Show errors to the user (404s are now handled at the API level)
      const errorMessage = 'Failed to load studio context';
      dispatch(setError(errorMessage));
    }
  }, [contextLoading, contextError, dispatch]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue: StudioContextValue = React.useMemo(
    () => ({
      // Current state
      currentStudio,
      userRole,
      permissions,
      recentStudios,
      isLoading,
      error,

      // Actions
      switchStudio,
      refreshStudio,
      clearStudio,
      clearContextError,

      // Utilities
      hasStudioAccess,
      canAccessFeature,
    }),
    [
      currentStudio,
      userRole,
      permissions,
      recentStudios,
      isLoading,
      error,
      switchStudio,
      refreshStudio,
      clearStudio,
      clearContextError,
      hasStudioAccess,
      canAccessFeature,
    ]
  );

  return (
    <StudioContext.Provider value={contextValue}>
      {children}
    </StudioContext.Provider>
  );
};

/**
 * Hook to access the studio context.
 * Must be used within a StudioProvider.
 */
export const useStudioContext = (): StudioContextValue => {
  const context = useContext(StudioContext);
  if (context === undefined) {
    throw new Error('useStudioContext must be used within a StudioProvider');
  }
  return context;
};

/**
 * Hook to get the current studio with type safety.
 * Returns null if no studio is selected.
 */
export const useCurrentStudio = () => {
  const { currentStudio, isLoading, error } = useStudioContext();
  return { currentStudio, isLoading, error };
};

/**
 * Hook to check if user has access to a specific studio.
 */
export const useStudioAccess = (studioId: string) => {
  const { hasStudioAccess } = useStudioContext();
  return hasStudioAccess(studioId);
};

/**
 * Hook to check if user can access a specific feature.
 */
export const useFeatureAccess = (feature: keyof StudioPermissions) => {
  const { canAccessFeature } = useStudioContext();
  return canAccessFeature(feature);
};

export default StudioContext;
